import Effect from "@editor/items/effect/Effect";
import Condition from "@editor/items/condition/Condition";

import type { SimulationModelState } from "@editor/items/model/ModelState";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";
import type { IncidentTriggerItem } from "@editor/items/incidentTrigger/IncidentTriggerItem";
import type { SimulationRoundItem } from "@editor/items/simulationRound/SimulationRoundItem";
import type { Uid } from "@/helpers/utility/Id";

interface OtherIncidentData {
  effects: Effect[];
  effectIds: (string | number)[];
  triggers: IncidentTriggerItem[];
  triggerConditions: { [incidentId: number]: Condition[] };
}

class Incident implements IncidentItem {
  id: number;
  uid: Uid<"incident">;
  createdAt: number;
  type: "incident";
  title: string;
  order: number;

  chance: number;
  description: string;
  maxOccurrences: number | null;

  imageId?: number | undefined;
  effectIds: (string | number)[];
  effects: Effect[];
  triggers: IncidentTriggerItem[];
  triggerConditions: { [incidentId: number]: Condition[] };
  
  effectsVisibility: "as-is" | "visible" | "hidden" | "values-hidden";
  visibility: "hidden" | "visible";

  constructor(incidentData: IncidentItem, otherData: OtherIncidentData) {
    this.id = incidentData.id;
    this.uid = incidentData.uid;
    this.createdAt = incidentData.createdAt;
    this.type = incidentData.type;
    this.title = incidentData.title;
    this.description = incidentData.description;
    this.order = incidentData.order;

    this.chance = incidentData.chance;
    this.maxOccurrences = incidentData.maxOccurrences;

    this.effectsVisibility = incidentData.effectsVisibility;
    this.visibility = incidentData.visibility;

    this.effectIds = otherData.effectIds;
    this.effects = otherData.effects;
    this.triggers = otherData.triggers;
    this.triggerConditions = otherData.triggerConditions;
  }

  getApplyingTrigger(rounds: SimulationRoundItem[]) {
    const round = rounds[rounds.length - 1];

    // check all incident triggers
    for (const trigger of this.triggers) {
      // ignore trigger if it has already been triggered enough
      const triggerCount = round.triggerCounts[trigger.id];
      if (
        triggerCount != null &&
        trigger.maxOccurrences !== null &&
        triggerCount >= trigger.maxOccurrences
      ) {
        continue;
      }

      // don't even check if the trigger has no chance to succeed
      if (trigger.chance <= 0) continue;

      const rolled = Math.round(Math.random() * 100);

      // stop if we didn't roll enough
      if (rolled > trigger.chance) {
        continue;
      }

      // check CONDITIONS of trigger
      const triggerConditions = this.triggerConditions[trigger.id];

      const allConditionsApply = triggerConditions.every((condition) =>
        condition.evaluate(rounds)
      );

      if (allConditionsApply == false) continue;

      // save the first condition as the reason why the incident was activated
      const triggeredCondition = triggerConditions?.[0];

      // return the trigger
      return {
        incidentId: this.id,
        reason: "trigger",
        rolledChance: rolled,
        neededChance: trigger.chance,
        conditionId: triggeredCondition?.id,
        triggerId: trigger.id
      };
    }

    return null;
  }

  static instanceFromStore(clonedModelState: SimulationModelState, id: number) {
    const item = clonedModelState.incidents.items[id];

    if (item == null) {
      return null;
    }

    const triggers = Object.values(clonedModelState.incidentTriggers.items).filter(
      (incidentTrigger) => incidentTrigger.incidentId == id
    );
    const triggerConditions: { [triggerId: number]: Condition[] } =
      triggers.reduce(
        (
          conditionsByTriggerId: { [triggerId: number]: Condition[] },
          trigger: IncidentTriggerItem
        ) => {
          conditionsByTriggerId[trigger.id] = Object.values(
            clonedModelState.conditions.items
          )
            .filter(
              (condition) =>
                condition.srcType == trigger.type &&
                condition.srcId == trigger.id
            )
            .map((conditionData) => new Condition(conditionData));

          return conditionsByTriggerId;
        },
        {}
      );

    // get effects
    const effects = Effect.filterByActivator(
      clonedModelState.effects.items,
      item
    ).map((effectData) => new Effect(effectData));

    const effectIds = effects.map((effect) => effect.id);

    return new Incident(item, {
      effects,
      effectIds,
      triggers,
      triggerConditions
    });
  }
}

/**
 * Clone an object removing any previous references to the original
 */
function clone<T>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}

export default Incident;
